// String formatting helpers
/**
 * Converts a snake_case identifier to a Title Case display label.
 * e.g. "daily_checkin" → "Daily Checkin", "survey_completion" → "Survey Completion".
 * Display-only — never use this to derive a value used in logic.
 */
export function snakeToTitleCase(input: string): string {
  return input
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Member-facing display label for a points_ledger `source_type`. Single source
 * of truth shared by every activity/history surface (Dashboard, Profile, and the
 * future all-earnings history) so the same event never renders two different
 * strings. Covers the full PointsSourceType enum; unmapped values fall back to a
 * Title Case rendering of the raw snake_case value. Display-only — never derive a
 * value used in logic from this.
 *
 * Note: render sites should prefer a row's own `note` when present
 * (`entry.note ?? ledgerLabel(entry.source_type)`) so descriptive per-event
 * captions (e.g. a game name) win over this generic label.
 */
export function ledgerLabel(sourceType: string): string {
  const map: Record<string, string> = {
    survey_completion: 'Survey completed',
    disqual_consolation: 'Disqualification bonus',
    signup_bonus: 'Signup bonus',
    referral: 'Referral bonus',
    redemption: 'Redemption',
    redemption_refund: 'Redemption refund',
    adjustment: 'Adjustment',
    offer_completion: 'Offer completed',
    game_completion: 'Game reward',
    game_reversal: 'Game reward reversed',
    survey_reversal: 'Survey reward reversed',
    streak_bonus: 'Streak milestone',
    daily_checkin: 'Daily check-in',
    onboarding_bonus: 'Onboarding bonus',
    profiler_bonus: 'Profiler bonus',
    writing_sample_bonus: 'Writing sample bonus',
    early_advance: 'Early advance',
    early_advance_forfeit: 'Early advance forfeit',
    support_chat: 'Support credit',
    support_goodwill: 'Support goodwill',
    support_owed: 'Owed points',
    support_ticket_award: 'Support award',
    gokart_offer: 'Offer completed',
    gokart_offer_reversal: 'Offer reward reversed',
    welcome_survey: 'Welcome survey',
    swipe_participation: 'Swipe reward',
  };
  return map[sourceType] ?? snakeToTitleCase(sourceType);
}

// Trust-level names — the single source of truth for the 0–100 trust-score
// level (L1–L5) display names. Consumed by the API (trustScoreService) and the
// web app (Profile card, Trust Center) so the name never drifts between
// surfaces. Level numbers come from getTrustLevel (>=91/>=61/>=41/>=26/else).
export const TRUST_LEVEL_NAMES: Record<number, string> = {
  1: 'Basic',
  2: 'Rising',
  3: 'Trusted',
  4: 'Elite',
  5: 'Champion',
};

// Trust-score recovery deltas — the single source of truth for the
// survey-completion trust bonuses. Consumed by the API (trustScoreService
// re-exports this as TRUST_DELTAS and the award code reads it) AND the web Trust
// Center copy (TrustCenterPage interpolates these numbers) so the advertised
// values can never drift from what the award code actually grants. Change a
// number here and both the grant and the copy move together.
export const TRUST_RECOVERY_DELTAS = {
  CLEAN_SURVEY: 1,
  CLEAN_SURVEY_DAILY_CAP: 5,
  // Leak 2 (2026-07-01): lifetime cap on clean_survey_complete trust so an
  // uncapped +1/completion can't drift a member up a tier over time. Enforced in
  // applySurveyCompletionBonuses by summing prior clean_survey_complete deltas.
  CLEAN_SURVEY_LIFETIME_CAP: 25,
  SURVEY_MILESTONE_10: 3,
  SURVEY_MILESTONE_25: 5,
  STREAK_7: 2,
  STREAK_30: 5,
} as const;

// Trust-score VERIFICATION deltas — the single source of truth for the one-time
// identity/verification trust awards. Consumed by the API award sites (signup /
// phone / video / writing) AND the web Trust Center card copy (TrustCenterPage
// VERIFICATION_DEFS advertises "+N trust score") so the advertised number can
// never drift from what the award code grants. Rebalanced 2026-07-01 (two-factor
// tier model): the soft-signal awards were lowered so the soft-only one-time
// ceiling lands at L3 (see the rebalance-invariant test); IDENTITY_VERIFIED is
// the large delta that Phase B grants once a real ID/biometric check exists.
export const TRUST_VERIFICATION_DELTAS = {
  SIGNUP: 5,
  PHONE_VERIFIED: 10,
  VIDEO_VERIFIED: 10,
  WRITING_SAMPLE: 10,
  IDENTITY_VERIFIED: 30, // defined now; only GRANTED in Phase B when a provider is wired
} as const;

// Onboarding reward figures — the single source of truth for the "bank N pts"
// onboarding copy so the UI can't drift from what the API grants. COMPLETION is
// the points banked at POST /onboarding/complete = Tier 1 (ONBOARDING_BONUS) +
// Tier 2 (PROFILE_SCORE_TIER2_BONUS, which auto-fires at completion). TOTAL is
// the max across onboarding + profile-completion tiers. An API invariant test
// asserts COMPLETION === ONBOARDING_BONUS + PROFILE_SCORE_TIER2_BONUS.
export const ONBOARDING_REWARD = {
  COMPLETION: 50, // 15 (Tier 1) + 35 (Tier 2)
  TOTAL: 100,
} as const;

// Member types
export type MemberStatus = 'active' | 'inactive' | 'suspended' | 'opted_out' | 'blocked';
export type AuthProvider = 'magic_link' | 'google';

export interface Member {
  id: string;
  supabase_uid: string;
  email: string | null;
  display_name: string | null;
  avatar_id: string | null;
  google_id: string | null;
  role: string;
  status: MemberStatus;
  total_points: number;
  fraud_score: number;
  onboarding_complete: boolean;
  streak_current: number;
  streak_longest: number;
  streak_last_activity_date: Date | null;
  referral_code: string | null;
  referred_by: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface DemographicProfile {
  id: string;
  member_id: string;
  date_of_birth: Date | null;
  gender: string | null;
  ethnicity: string | null;
  country: string | null;
  state: string | null;
  zip_code: string | null;
  household_income: string | null;
  education: string | null;
  employment_status: string | null;
  marital_status: string | null;
  household_size: number | null;
  created_at: Date;
  updated_at: Date;
}

export interface PsychographicProfile {
  id: string;
  member_id: string;
  interests: string[];
  values: string[];
  lifestyle: string | null;
  personality_traits: string[];
  created_at: Date;
  updated_at: Date;
}

export interface BehavioralProfile {
  id: string;
  member_id: string;
  avg_session_duration_seconds: number;
  surveys_completed: number;
  surveys_disqualified: number;
  avg_completion_rate: number;
  preferred_survey_length: string | null;
  active_hours: number[];
  last_active_at: Date | null;
  created_at: Date;
  updated_at: Date;
}

export interface MemberAttribute {
  id: string;
  member_id: string;
  attribute_key: string;
  attribute_value: string;
  source: 'explicit' | 'inferred' | 'prequal';
  created_at: Date;
  updated_at: Date;
}

export interface InferredAttribute {
  id: string;
  member_id: string;
  attribute_key: string;
  attribute_value: string;
  confidence_score: number;
  model_version: string | null;
  created_at: Date;
}

export type SurveyStatus = 'active' | 'paused' | 'closed' | 'draft';

export interface Survey {
  id: string;
  provider_id: string;
  provider_name: string;
  title: string;
  description: string | null;
  category: string | null;
  survey_url: string | null;
  points_value: number;
  consolation_points: number;
  estimated_length_minutes: number;
  status: SurveyStatus;
  created_at: Date;
  updated_at: Date;
}

export interface SurveyTargetingRule {
  id: string;
  survey_id: string;
  attribute_key: string;
  operator: 'eq' | 'neq' | 'in' | 'nin' | 'gt' | 'lt' | 'gte' | 'lte';
  attribute_value: string;
  is_hard_filter: boolean;
  created_at: Date;
}

export interface PrequalifierQuestion {
  id: string;
  survey_id: string;
  question_text: string;
  question_type: 'single_choice' | 'multi_choice' | 'text' | 'number';
  options: string[];
  maps_to_attribute: string | null;
  disqualifying_answers: string[];
  sort_order: number;
  created_at: Date;
}

export type AssignmentStatus = 'assigned' | 'started' | 'completed' | 'disqualified' | 'abandoned';

export interface SurveyAssignment {
  id: string;
  member_id: string;
  survey_id: string;
  status: AssignmentStatus;
  assigned_at: Date;
  started_at: Date | null;
  completed_at: Date | null;
  points_awarded: number;
  provider_ref: string | null;
  disqual_reason: DisqualReason | null;
}

export type PointsSourceType =
  | 'survey_completion'
  | 'disqual_consolation'
  | 'signup_bonus'
  | 'referral'
  | 'redemption'
  | 'redemption_refund'
  | 'adjustment'
  | 'offer_completion'
  | 'game_completion'
  | 'game_reversal'
  | 'survey_reversal'
  | 'streak_bonus'
  | 'daily_checkin'
  | 'onboarding_bonus'
  | 'profiler_bonus'
  | 'writing_sample_bonus'
  | 'early_advance'
  | 'early_advance_forfeit'
  | 'support_chat'
  | 'support_goodwill'
  | 'support_owed'
  | 'support_ticket_award'
  | 'gokart_offer'
  | 'gokart_offer_reversal'
  | 'welcome_survey'
  | 'swipe_participation';

export interface WelcomeSurveyQuestion {
  qualCode: number;
  questionText: string;
  questionType: string;
  options: Array<{ label: string; conditionCodes: string[]; value: string }>;
  attributeKey: string;
}

export type RedemptionType = 'gift_card' | 'paypal' | 'bank';
export type RedemptionStatus = 'pending' | 'held' | 'approved' | 'processing' | 'fulfilled' | 'failed' | 'refunded';

export interface Reward {
  id: string;
  name: string;
  type: RedemptionType;
  points_cost: number;
  usd_value: number;
  active: boolean;
  created_at: Date;
  updated_at: Date;
}

export interface Redemption {
  id: string;
  member_id: string;
  type: RedemptionType;
  reward_id: string;
  points_cost: number;
  usd_value: number;
  status: RedemptionStatus;
  external_ref: string | null;
  created_at: Date;
  fulfilled_at: Date | null;
  error_msg: string | null;
}

export interface CreateRedemptionRequest {
  reward_id: string;
}

export interface CreateRedemptionResponse {
  redemption: Redemption;
}

export interface RedemptionListResponse {
  redemptions: Redemption[];
  total: number;
}

export interface PointsLedger {
  id: string;
  member_id: string;
  delta: number;
  balance_after: number;
  source_type: PointsSourceType;
  source_id: string | null;
  note: string | null;
  is_pending: boolean;
  pending_until: Date | null;
  created_at: Date;
}

// ── Ledger categories ────────────────────────────────────────────────────────
//
// The earnings-history filter groups points_ledger rows by CATEGORY, never by
// raw source_type. This is the single source of truth: a new PointsSourceType is
// classified by adding ONE entry here and the filter picks it up automatically.
// The UI reads categories; it never enumerates source types.

export type LedgerCategory =
  | 'survey'
  | 'game'
  | 'offer'
  | 'bonus'
  | 'redemption'
  | 'support'
  | 'adjustment'
  | 'other';

// Every current PointsSourceType maps to a category. An unmapped value resolves
// to 'other' (see ledgerCategory) so a new source type can never silently vanish
// from the history — it shows under "Other" until it's classified here.
const LEDGER_CATEGORY_BY_SOURCE: Record<string, LedgerCategory> = {
  // survey — completions, consolation, reversals, welcome survey, and the swipe
  // profiler (a survey-adjacent earning surface).
  survey_completion: 'survey',
  disqual_consolation: 'survey',
  survey_reversal: 'survey',
  welcome_survey: 'survey',
  swipe_participation: 'survey',
  // game — Besitos + GoKart offer walls and their reversals.
  game_completion: 'game',
  game_reversal: 'game',
  gokart_offer: 'game',
  gokart_offer_reversal: 'game',
  // offer — reserved for a future dedicated offerwall (thinly populated today).
  offer_completion: 'offer',
  // bonus — every non-earned incentive grant.
  signup_bonus: 'bonus',
  referral: 'bonus',
  streak_bonus: 'bonus',
  daily_checkin: 'bonus',
  onboarding_bonus: 'bonus',
  profiler_bonus: 'bonus',
  writing_sample_bonus: 'bonus',
  early_advance: 'bonus',
  early_advance_forfeit: 'bonus',
  // redemption — cash-outs and their refunds.
  redemption: 'redemption',
  redemption_refund: 'redemption',
  // support — member-care credits.
  support_chat: 'support',
  support_goodwill: 'support',
  support_owed: 'support',
  support_ticket_award: 'support',
  // adjustment — manual admin corrections.
  adjustment: 'adjustment',
};

/** Category for a ledger source_type. Unknown/new values → 'other'. */
export function ledgerCategory(sourceType: string): LedgerCategory {
  return LEDGER_CATEGORY_BY_SOURCE[sourceType] ?? 'other';
}

/** All source_types in a category — used by the API to translate a category
 *  filter into a `source_type IN (...)` query. 'other' returns []. */
export function sourceTypesForCategory(category: LedgerCategory): string[] {
  return Object.entries(LEDGER_CATEGORY_BY_SOURCE)
    .filter(([, c]) => c === category)
    .map(([s]) => s);
}

/** Member-facing labels for the category filter chips. */
export const LEDGER_CATEGORY_LABELS: Record<LedgerCategory, string> = {
  survey: 'Surveys',
  game: 'Games',
  offer: 'Offers',
  bonus: 'Bonuses',
  redemption: 'Redemptions',
  support: 'Support',
  adjustment: 'Adjustments',
  other: 'Other',
};

/** Stable display order for the filter chips (only those with rows are shown). */
export const LEDGER_CATEGORY_ORDER: LedgerCategory[] = [
  'survey', 'game', 'offer', 'bonus', 'redemption', 'support', 'adjustment', 'other',
];

/**
 * Member-facing outcome label for a NON-crediting survey session — the Ship 2.5
 * "activity" rows on /earning-history (attempts that produced no points_ledger
 * row). Reuses the pre-Ship-2 SurveyHistoryPage wording. All render as a neutral
 * (gray) pill; points are never shown on these rows. Unknown/unmapped statuses
 * fall back to 'Ended early' so a new terminal status never renders blank.
 *
 * NOT included (never reach an activity row): 'complete'/'reversed' (deduped to
 * their ledger entries), 'pending' (in-progress), 'replay_attack' (a security
 * rejection, filtered out at the query — not a member action).
 */
export function activityOutcomeLabel(status: string): string {
  switch (status) {
    case 'screener_fail': return 'Not a fit';
    case 'quota_full': return 'Quota full';
    case 'quality_fail': return 'Ended early';
    case 'abandoned': return "Didn't finish";
    case 'unknown': return 'Ended early';
    default: return 'Ended early';
  }
}

// Profile attribute keys
export type ProfileAttributeKey =
  | 'date_of_birth'
  | 'age'          // derived — auto-set when date_of_birth is saved
  | 'age_band'     // derived — auto-set when date_of_birth is saved
  | 'gender'
  | 'zip_code'
  | 'state'        // derived — auto-set when zip_code is saved
  | 'dma'          // derived — auto-set when zip_code is saved
  | 'region'       // derived — auto-set when zip_code is saved
  | 'hispanic_origin'
  | 'race'
  | 'education'
  | 'employment'         // deprecated alias of employment_status
  | 'employment_status'
  | 'household_size'
  | 'hhi'
  | 'income_band'        // deprecated alias of hhi (kept for legacy callers)
  | 'marital_status'
  | 'has_children'
  | 'child_birth_years'
  | 'children_count'
  | 'interests'
  | 'hobbies'
  | 'pets'               // deprecated alias of pet_ownership
  | 'pet_ownership'
  | 'home_ownership'
  | 'vehicle_type'       // deprecated alias of auto_type
  | 'vehicle_ownership'  // deprecated alias of auto_type
  | 'auto_type'
  | 'auto_make'
  | 'political_leaning'
  | 'military_status'
  | 'smoking_status'
  // Onboarding incentive redesign (2026-05-11) — enrichment-only keys
  // newly accepted by POST /api/profile/attributes so they can count
  // toward the tiered profile score.
  | 'language_at_home'
  | 'voter_registration'
  | 'smartphone_type'
  | 'social_media_use';

export type ProfileCategory = 'demographic' | 'psychographic' | 'behavioral' | 'household';

// Keys must be CANONICAL attribute keys (employment_status / hhi /
// pet_ownership / auto_type), not the retired aliases — completion scoring
// does a binary key-presence check. See 2026-05-15 profile-page audit.
export const PROFILE_CATEGORIES: Record<ProfileCategory, ProfileAttributeKey[]> = {
  demographic: ['date_of_birth', 'gender', 'zip_code', 'hispanic_origin', 'race', 'education', 'employment_status'],
  psychographic: ['interests', 'hobbies', 'political_leaning'],
  // Mirrors the "Home & Habits" profile form section so the ring bar and the
  // section the member fills correspond 1:1 (auto_make + smoking_status were
  // rendered in that section but absent from this denominator — the source of
  // the "all filled but 67%" confusion).
  behavioral: ['pet_ownership', 'home_ownership', 'auto_type', 'auto_make', 'smoking_status'],
  household: ['household_size', 'hhi', 'marital_status', 'has_children', 'child_birth_years'],
};

export interface ProfileCompletion {
  overall: number;
  categories: Record<ProfileCategory, number>;
}

// ── Profile form field options ──────────────────────────────────────────────
//
// Option `value` strings MUST match the canonical valid_values in
// apps/api/src/lib/attributeSchema.ts. Display `label` is UI-friendly.
// Drift is caught by apps/api/src/test/profileFieldOptions.unit.test.ts —
// that test fails CI if any option here isn't a canonical value, so the
// frontend dropdown never falls out of sync again.

export interface ProfileFieldOption {
  value: string;
  label: string;
}

/** Canonical values: full_time, part_time, self_employed, unemployed, student, retired, homemaker. */
export const EMPLOYMENT_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'self_employed', label: 'Self-Employed / Contract' },
  { value: 'student', label: 'Student' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'retired', label: 'Retired' },
  { value: 'homemaker', label: 'Homemaker' },
];

/** Canonical values: some_high_school, high_school, some_college, bachelors,
 *  masters, doctorate. NOTE: canonical is 'high_school' (NOT 'high_school_grad')
 *  — the onboarding pills MUST emit these or saveCanonicalAttribute drops the
 *  answer as invalid_value (same silent-drop class as the employment gap). */
export const EDUCATION_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'some_high_school', label: 'Some high school' },
  { value: 'high_school', label: 'High school graduate' },
  { value: 'some_college', label: 'Some college' },
  { value: 'bachelors', label: "Bachelor's degree" },
  { value: 'masters', label: "Master's degree" },
  { value: 'doctorate', label: 'Doctorate' },
];

/**
 * HHI is stored canonically as a numeric lower bound (e.g. onboarding's
 * "$50,000 – $74,999" → 50000). These option values exactly match the
 * bucket floors from apps/api/src/constants/psAttributeMap.ts HHI.buckets
 * so the select round-trips without translation.
 */
export const HHI_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: '7500', label: 'Under $15,000' },
  { value: '15000', label: '$15,000 \u2013 $24,999' },
  { value: '25000', label: '$25,000 \u2013 $34,999' },
  { value: '35000', label: '$35,000 \u2013 $49,999' },
  { value: '50000', label: '$50,000 \u2013 $74,999' },
  { value: '75000', label: '$75,000 \u2013 $99,999' },
  { value: '100000', label: '$100,000 \u2013 $124,999' },
  { value: '125000', label: '$125,000 \u2013 $149,999' },
  { value: '150000', label: '$150,000 \u2013 $199,999' },
  { value: '200000', label: '$200,000 \u2013 $249,999' },
  { value: '250000', label: '$250,000 or more' },
  { value: '-1', label: 'Prefer not to say' },
];

/** Canonical AUTO_TYPE_OPTIONS from attributeSchema.ts — 'electric_hybrid'
 *  is a single combined value (Prius etc.), distinct from 'electric' (Tesla). */
export const AUTO_TYPE_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'coupe', label: 'Coupe' },
  { value: 'suv', label: 'SUV/Crossover' },
  { value: 'truck', label: 'Truck' },
  { value: 'minivan', label: 'Minivan' },
  { value: 'van', label: 'Van' },
  { value: 'sports', label: 'Sports Car' },
  { value: 'convertible', label: 'Convertible' },
  { value: 'hatchback', label: 'Hatchback' },
  { value: 'station_wagon', label: 'Station wagon' },
  { value: 'electric', label: 'Electric' },
  { value: 'electric_hybrid', label: 'Hybrid / Electric' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'None' },
];

/** Canonical AUTO_MAKE_OPTIONS from attributeSchema.ts. Onboarding
 *  incentive redesign 2026-05-11 — the make field is enrichment-only
 *  (no onboarding step). 'none' counts as filled (explicit opt-out). */
export const AUTO_MAKE_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'toyota', label: 'Toyota' },
  { value: 'honda', label: 'Honda' },
  { value: 'ford', label: 'Ford' },
  { value: 'chevrolet', label: 'Chevrolet' },
  { value: 'gmc', label: 'GMC' },
  { value: 'ram', label: 'Ram' },
  { value: 'nissan', label: 'Nissan' },
  { value: 'jeep', label: 'Jeep' },
  { value: 'subaru', label: 'Subaru' },
  { value: 'hyundai', label: 'Hyundai' },
  { value: 'kia', label: 'Kia' },
  { value: 'volkswagen', label: 'Volkswagen' },
  { value: 'bmw', label: 'BMW' },
  { value: 'mercedes_benz', label: 'Mercedes-Benz' },
  { value: 'audi', label: 'Audi' },
  { value: 'lexus', label: 'Lexus' },
  { value: 'tesla', label: 'Tesla' },
  { value: 'mazda', label: 'Mazda' },
  { value: 'dodge', label: 'Dodge' },
  { value: 'chrysler', label: 'Chrysler' },
  { value: 'buick', label: 'Buick' },
  { value: 'cadillac', label: 'Cadillac' },
  { value: 'acura', label: 'Acura' },
  { value: 'infiniti', label: 'Infiniti' },
  { value: 'volvo', label: 'Volvo' },
  { value: 'land_rover', label: 'Land Rover' },
  { value: 'porsche', label: 'Porsche' },
  { value: 'lincoln', label: 'Lincoln' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'None' },
];

/** Canonical PET_OPTIONS from attributeSchema.ts. */
export const PET_OWNERSHIP_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'bird', label: 'Bird' },
  { value: 'fish', label: 'Fish' },
  { value: 'amphibian', label: 'Amphibian' },
  { value: 'small_animal', label: 'Small animal' },
  { value: 'reptile', label: 'Reptile' },
  { value: 'horse', label: 'Horse' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'None' },
];

/** Canonical MILITARY_STATUS_OPTIONS from attributeSchema.ts. */
export const MILITARY_STATUS_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'civilian', label: 'No military background' },
  { value: 'active', label: 'Active duty' },
  { value: 'veteran', label: 'Veteran' },
  { value: 'retired_service', label: 'Retired servicemember' },
  { value: 'reserves', label: 'Reserves / National Guard' },
  { value: 'military_spouse', label: 'Military spouse' },
];

/** Canonical SMOKING_OPTIONS from attributeSchema.ts. */
export const SMOKING_STATUS_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'current', label: 'Current smoker' },
  { value: 'former', label: 'Former smoker' },
  { value: 'never', label: 'Never smoked' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];

/** Canonical INTEREST_OPTIONS from attributeSchema.ts (multi_select).
 *  Values are the lowercase canonical codes stored in member_attribute —
 *  ProfilePage's ChipPicker must match + save on `value`, not `label`. */
export const INTERESTS_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'sports', label: 'Sports' },
  { value: 'movies', label: 'Movies' },
  { value: 'music', label: 'Music' },
  { value: 'art', label: 'Art' },
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'food', label: 'Food' },
  { value: 'travel', label: 'Travel' },
  { value: 'politics', label: 'Politics' },
  { value: 'science', label: 'Science' },
  { value: 'history', label: 'History' },
  { value: 'outdoors', label: 'Outdoors' },
  { value: 'reading', label: 'Reading' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'gardening', label: 'Gardening' },
  { value: 'other', label: 'Other' },
];

/** Canonical HOBBY_OPTIONS from attributeSchema.ts (multi_select). */
export const HOBBIES_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'cooking', label: 'Cooking' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'reading', label: 'Reading' },
  { value: 'music', label: 'Music' },
  { value: 'sports', label: 'Sports' },
  { value: 'team_sports', label: 'Team sports' },
  { value: 'travel', label: 'Travel' },
  { value: 'photography', label: 'Photography' },
  { value: 'volunteering', label: 'Volunteering' },
  { value: 'gardening', label: 'Gardening' },
  { value: 'arts_crafts', label: 'Arts & crafts' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'outdoors', label: 'Outdoors' },
  { value: 'other', label: 'Other' },
];

/** Canonical LANGUAGE_OPTIONS from attributeSchema.ts (single_select). */
export const LANGUAGE_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'english_all', label: 'English all the time' },
  { value: 'english_most', label: 'English more than half the time' },
  { value: 'spanish_and_english', label: 'Spanish and English equally' },
  { value: 'spanish_most', label: 'Spanish more than half the time' },
  { value: 'spanish_all', label: 'Spanish all the time' },
];

/** Canonical SMARTPHONE_OPTIONS from attributeSchema.ts (single_select). */
export const SMARTPHONE_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'iphone', label: 'iPhone' },
  { value: 'android', label: 'Android' },
  { value: 'google_pixel', label: 'Google Pixel' },
  { value: 'blackberry', label: 'BlackBerry' },
  { value: 'windows_mobile', label: 'Windows Mobile' },
  { value: 'other', label: 'Other' },
  { value: 'no_smartphone', label: "I don't own a smartphone" },
];

/** Canonical SOCIAL_MEDIA_OPTIONS from attributeSchema.ts (multi_select). */
export const SOCIAL_MEDIA_FIELD_OPTIONS: ProfileFieldOption[] = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'tumblr', label: 'Tumblr' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'snapchat', label: 'Snapchat' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'spotify', label: 'Spotify' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'None' },
];

export interface UpsertAttributeRequest {
  key: ProfileAttributeKey;
  value: string;
}

export type DisqualReason = 'quota_full' | 'screener_fail' | 'quality_fail' | 'unknown';

// Survey engine types
export interface UnknownQualOption {
  code: string;
  label: string;
}

export interface UnknownQual {
  qualCode: number;
  question: string;
  type: string;
  options: UnknownQualOption[];
  required_codes?: string[];
  required_ranges?: Array<{ from: number; to: number }>;
  /** Canonical attribute key the answer should be saved under. Always set
   *  for RFG (which has no numeric qualCode); optional for PS where the
   *  qualCode itself is the canonical identifier. */
  attributeKey?: string;
  /** Source exchange — frontend dispatches answer submission accordingly. */
  exchange?: 'ps' | 'rfg';
}

export interface RankedSurvey extends Survey {
  score: number;
  pts_per_minute: number;
  attribute_match_pct: number;
  prescreen: boolean;
  isTest?: boolean;
  unknownQuals?: UnknownQual[];
  unknown_quals_count?: number;
  loi?: number;
  ir?: number;
  registered_at?: string | null;
  ir_last_block?: number;
  currently_open_min?: number;
  /** 'ps' | 'rfg' — which exchange this survey originated from. */
  exchange?: string;
  /** API path the frontend calls to start this survey (e.g. /api/rfg/redirect). */
  entryPath?: string;
  /** Points at the BASE (L1-L2) rate. For L3-L5 members this is < points_value
   *  (which uses their elevated rate); the card strikes it through to show the
   *  trust bonus. Equals points_value for L1-L2 → no strikethrough. */
  base_points?: number;
}

// Pending points types
export interface PendingSummaryEntry {
  id: string;
  points: number;
  earnedAt: string;
  pendingUntil: string;
  daysSinceEarned: number;
  advanceablePercent: number;
  advanceablePoints: number;
}

export interface PendingSummary {
  totalPending: number;
  totalAdvanceable: number;
  entries: PendingSummaryEntry[];
}

export interface PrequalAnswer {
  questionId: string;
  answer: string;
}

export interface PrequalSubmitResult {
  qualifies: boolean;
  surveyUrl: string | null;
}

// AdGate Media offer types
export interface AdGateOfferEvent {
  id: string;
  name: string;
  payout_type: string;
  payout: number;
}

export interface AdGateOffer {
  id: number;
  anchor: string;
  description: string;
  requirements: string;
  pending_time: number;
  creatives: { icon: string };
  epc: number;
  categories: string[];
  events: AdGateOfferEvent[];
  click_url: string;
  geo_targeting: { countries: { country_code: string }[] };
  demographic_targeting: {
    age: { options: { min_age: number; max_age: number }[] };
    gender: { options: string[] };
  };
  session_hours: number;
}

export interface DisplayOfferEvent {
  id: string;
  name: string;
  points: number;
  // GoKart sequence_number: ordered funnel step, or null for independent /
  // "whale" milestones. Server pre-sorts (asc, nulls last); null renders as an
  // independent milestone rather than a numbered step.
  sequence?: number | null;
}

export interface DisplayOffer {
  // GoKart offer ids are UUIDs (the offer's gokart_offer_id) — string, not the
  // legacy numeric AdGate id. This is the handle the click handshake takes.
  id: string;
  title: string;
  // Clean, human-readable copy (never the raw provider {"ar":...} blob).
  // `description` is the readable body; `headline`/`keyPoints` are the clean
  // wire fields (or recovered from the blob's `kp`). Additive — the UI can
  // render headline/keyPoints when present, else falls back to description.
  description: string;
  headline?: string | null;
  keyPoints?: string[];
  // Best-effort banner from the raw blob's creative (cre[].cov/car[]); null when
  // none — the card/modal fall back to icon_url. Never a broken URL.
  bannerUrl?: string | null;
  // App-store rating (blob `ar`, 0–5); decorative, null when absent.
  rating?: number | null;
  requirements: string;
  icon_url: string;
  max_points: number;
  events: DisplayOfferEvent[];
  // GoKart offers have NO static click url — it is resolved per-click via the
  // click handshake (POST /api/gokart/offers/:id/click). Served empty here.
  click_url: string;
  // Additive (GoKart): flattened category for display/filter. null when unknown.
  category: string | null;
}

// Besitos game types
export interface GameGoal {
  goalId: string;
  amount: number;
  points: number;
  text: string;
  daysLeft: number | null;
  section: string;
  // GoKart sequence_number (ordered step, or null for independent milestone).
  // Besitos goals omit this (they use `section`).
  sequence?: number | null;
}

export interface DisplayGame {
  id: string;
  title: string;
  description: string;
  // Clean GoKart copy (additive; Besitos games omit these). `description` is
  // the readable body, never the raw provider {"ar":...} blob.
  headline?: string | null;
  keyPoints?: string[];
  // Best-effort banner from the raw blob's creative; null => fall back to
  // imageUrl. Rating is the blob `ar` (0–5), decorative.
  bannerUrl?: string | null;
  rating?: number | null;
  imageUrl: string;
  thumbnailUrl: string;
  category: string;
  device: string;
  maxPoints: number;
  redirectUrl: string;
  isNew: boolean;
  cardText: string | null;
  goals: GameGoal[];
  videoUrl: string | null;
  thumbnailVideoUrl: string | null;
  /** Raw Besitos budget_status — typically 'Active' or 'Exhausted'. Exhausted
   *  offers are filtered out of the wall before this point, but the field is
   *  surfaced for client-side filtering and admin diagnostics. */
  budgetStatus: string | null;
  /** Which provider this game came from. 'besitos' uses a static redirectUrl;
   *  'gokart' has no static url — its redirectUrl is resolved per-click via the
   *  GoKart click handshake. Defaults to 'besitos' when omitted (legacy). */
  source?: 'besitos' | 'gokart';
}

// API response types
export interface MeResponse {
  member: Member;
  points_balance: number;
}

export interface ApiError {
  error: string;
  code: string;
  statusCode: number;
}
